import { useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import BenchmarkRange from '../../BenchmarkRange';
import ChartDataAlert from '../../ChartDataAlert';

import { getProcessedMeta } from '../../../../../utilities/common/chart';
import { useDeviceType } from '../../../../../utilities/common/deviceType';
import useFilters from '../../../../../utilities/hooks/useFilters';
import { useFilterData } from '../../../../../utilities/hooks/useFilterData';
import {
  BarChartMetaData,
  WidgetDefinition,
} from '../../../../../shared/types/widget';
import { ChartDatasetIndex } from '../../../../../shared/constants/chart';
import {
  createTrendLineDataset,
  createYScaleTextPlugin,
  getBarChartOptions,
} from './config';

import respondent from '../../../../../assets/OrgDim/icons/respondents-indicator.svg';
import Switch from '../../../../widgets/Switch';

interface BarChartProps {
  definition: WidgetDefinition;
  content: BarChartMetaData;
}

function BarChartCurve({ definition, content }: BarChartProps) {
  const [isBenchmarkEnabled, setIsBenchmarkEnabled] = useState(true);
  const [showRespondentsLine, setShowRespondentsLine] = useState(true);

  const { benchmarkType } = useFilters();
  const { configData } = useFilterData();
  const isMobile = useDeviceType();
  const { t } = useTranslation();

  // First dataset is bar, second is line respondents
  const yScaleTextPlugin = createYScaleTextPlugin({
    yLabel: content?.data?.datasets?.[ChartDatasetIndex.DATA_INDEX]?.label,
    y1Label:
      content?.data?.datasets?.[ChartDatasetIndex.RESPONDENT_INDEX]?.label,
  });

  const filteredMeta: BarChartMetaData | undefined = content?.data
    ? {
        ...content,
        data: {
          ...content.data,
          datasets: content.data.datasets.slice(0, 1).map((ds) => ({
            ...ds,
            type: 'bar',
            order: 1,
          })),
        },
      }
    : undefined;

  const processedMeta = useMemo(() => {
    if (!content?.data) {
      return {
        ...content,
        data: {
          ...content.data,
          datasets: [],
        },
      };
    }

    // Curve line config
    const trendLineDataset = createTrendLineDataset(
      showRespondentsLine,
      content.data.datasets[1]?.data,
    );

    // Process data for chartJs
    return getProcessedMeta({
      meta: filteredMeta!,
      additionalDatasets: [trendLineDataset],
      headCount: configData?.minimumResponseThreshold,
      respondents: content.data.datasets[1]?.data,
    });
  }, [content, showRespondentsLine]);

  const datasetValues =
    processedMeta?.data?.datasets?.[ChartDatasetIndex.RESPONDENT_INDEX]?.data ??
    [];

  const hasBelowThreshold = datasetValues.some(
    (v) => typeof v === 'number' && v < configData?.minimumResponseThreshold!,
  );

  // ChartJs options
  const options: any = useMemo(() => {
    if (!configData) return {};
    return getBarChartOptions({
      isBenchmarkEnabled,
      isMobile,
      meta: content,
      configData,
      benchmarkType,
      t,
    });
  }, [isBenchmarkEnabled, isMobile, content, configData, benchmarkType]);

  const ChartContent = useMemo(() => {
    return (
      <>
        <div
          className="mt-3 mb-6 flex w-full flex-row items-center justify-center gap-8"
          data-html2canvas-ignore="true"
        >
          <Switch
            label={t('BENCHMARKS')}
            checked={isBenchmarkEnabled}
            onChange={(checked) => setIsBenchmarkEnabled(checked)}
          />

          <Switch
            label={`${t('UNIQUE_RESPONDENTS')}`}
            checked={showRespondentsLine}
            onChange={(checked) => setShowRespondentsLine(checked)}
          />
        </div>
        <ChartDataAlert
          show={hasBelowThreshold}
          title={t('VERTICAL_CHART_DATA_ALERT_TITLE')}
          subTitle={t('VERTICAL_CHART_DATA_ALERT_DESCRIPTION')}
        />

        <div className="intl-bar-chart-scrollable relative mb-5 min-h-[350px] w-full overflow-auto py-6">
          <Chart
            type="bar"
            data={processedMeta.data}
            className="max-h-[322px] w-full"
            options={options}
            plugins={[yScaleTextPlugin]}
          />

          <div className="mt-12 flex flex-col gap-4 px-6">
            <div className="flex items-center">
              <p className="min-w-24 font-medium">
                {content?.data?.datasets?.[ChartDatasetIndex.DATA_INDEX]?.label}
                :
              </p>
              <BenchmarkRange meta={content?.meta} className="gap-x-6" />
            </div>
            <div className="flex gap-x-6">
              <span className="font-medium">
                {
                  content?.data?.datasets?.[ChartDatasetIndex.RESPONDENT_INDEX]
                    ?.label
                }
                :
              </span>
              <img src={respondent} alt="" />
            </div>
          </div>
        </div>
      </>
    );
  }, [
    processedMeta,
    options,
    content,
    isBenchmarkEnabled,
    showRespondentsLine,
  ]);

  return <>{ChartContent}</>;
}

export default BarChartCurve;
